import type { TableFilter } from '@/components/UI/Table/Reusable/APITable/APITable.model';
import { FilterTypes } from '@/constants/FilterTypes';
import { _isNotBlank } from '@/littledash';
import type { AsyncExportState } from '@/model/AsyncExport.model';
import { mapTimepointIntervalOperation } from '@/utils/TableFilter.utils';

const METADATA_PREFIX = 'metadata.';

const operatorFor = (name: FilterTypes): string | null =>
  Object.keys(FilterTypes).find((key) => FilterTypes[key as keyof typeof FilterTypes] === name) ?? null;

/**
 * A between bound is present when it is a number or a non-blank string. Blank or missing
 * bounds are dropped so we never emit a malformed `field|between|,` string (BNCH-162468).
 */
const hasBound = (bound: string | number | undefined): boolean => typeof bound === 'number' || _isNotBlank(bound);

const encodeFilter = (filter: TableFilter, multiSelectMetadataFieldIds: Set<string>): string | null => {
  const category = String(filter.category.value);
  if (category.includes(METADATA_PREFIX)) {
    return null;
  }

  const rawOperator = operatorFor(filter.operation.name);
  // Timepoint filters use interval-suffixed comparators the backend rejects; normalize
  // them to the base comparator, matching the list fetch path (BNCH-162468).
  const operator =
    rawOperator !== null ? mapTimepointIntervalOperation(filter.category.value, rawOperator) : rawOperator;
  const { value, from, to } = filter.option;

  // `between_datetime` shares the {from, to} option shape with `between`/`between_interval`
  // — encode all three the same way. Without this, the audit-log Timestamp filter (its only
  // range comparator) would fall through to the `value` branch and silently drop from the
  // export payload.
  if (operator === 'between' || operator === 'between_interval' || operator === 'between_datetime') {
    if (hasBound(from) && hasBound(to)) {
      return `${filter.category.value}|${operator}|${from},${to}`;
    }
    return null;
  }

  if (operator !== null && _isNotBlank(value)) {
    const filterValue = value;
    return `${filter.category.value}|${operator}|${filterValue}`;
  }

  if (filter.operation.name === FilterTypes.IS_NULL || filter.operation.name === FilterTypes.EMPTY) {
    return `${filter.category.value}|eq|null`;
  }

  if (filter.operation.name === FilterTypes.IS_NOT_NULL) {
    return `${filter.category.value}|ne|null`;
  }

  return null;
};

const EXPORT_LIMIT_MESSAGE_MARKER = 'too many exports in progress';

export const EXPORT_LIMIT_TOAST_MESSAGE =
  'Too many exports already in progress. Please wait for one to finish before starting another.';

export const isExportLimitError = (error: unknown): boolean => {
  const errors = (error as { response?: { data?: { errors?: Record<string, Array<string>> } } })?.response?.data
    ?.errors;
  if (errors === undefined) {
    return false;
  }
  return Object.values(errors).some((messages) =>
    messages.some((message) => message.includes(EXPORT_LIMIT_MESSAGE_MARKER))
  );
};

/**
 * Flattens APITable view filters into the `field|op|value` strings that the
 * legacy PHP filter pipeline expects. Returns null when the export route opts
 * out of filters (the "All X" variants) or when nothing is encodable. The
 * branching rules mirror `generateFilterParams` in `APITable.utils.ts`.
 *
 * @param multiSelectMetadataFieldIds - ids of the table's multi-select metadata fields, whose
 * values need URL encoding to match how the backend stores them
 */
export const buildAsyncExportFilterStrings = (
  state: AsyncExportState,
  multiSelectMetadataFieldIds: Set<string> = new Set()
): Array<string> | null => {
  if (state.route.noFilters === true) {
    return null;
  }
  const strings = (state.view.filters ?? []).reduce<Array<string>>((acc, filter) => {
    const encoded = encodeFilter(filter, multiSelectMetadataFieldIds);
    if (encoded !== null) {
      acc.push(encoded);
    }
    return acc;
  }, []);
  return strings.length > 0 ? strings : null;
};
